import { useRef, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * Full-bleed cinematic cover video for the top of a page. The clip fills a fixed-height
 * band and fades into the page background at the bottom, with a caption (names/date)
 * overlaid over the fade — so the video reads as the page's opening rather than an inserted
 * box. Autoplays muted (browser policy); a corner button unmutes its own audio on tap.
 *
 * The unmute control sits top-LEFT on purpose: the app's "Sign out" button owns top-right.
 */
export function VideoHero({
  src,
  poster,
  height = '66svh',
  children,
}: {
  src: string;
  poster?: string;
  /** Height of the cover band. */
  height?: string;
  /** Overlaid caption, rendered bottom-centre over the fade. */
  children?: ReactNode;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);

  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (!v.muted) void v.play();
  };

  return (
    <div style={{ position: 'relative', background: 'var(--bg)' }}>
      <video
        ref={ref}
        autoPlay
        muted
        loop
        playsInline
        poster={poster}
        style={{ width: '100%', height, objectFit: 'cover', display: 'block' }}
      >
        <source src={src} type="video/mp4" />
      </video>

      {/* Legibility + blend: subtle at top, deepening to the page bg at the bottom. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background:
            'linear-gradient(180deg, rgba(10,10,10,0.15) 30%, rgba(10,10,10,0.55) 70%, var(--bg) 100%)',
        }}
      />

      {children && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 28,
            textAlign: 'center',
            pointerEvents: 'none',
            textShadow: '0 2px 18px rgba(0,0,0,0.45)',
          }}
        >
          {children}
        </div>
      )}

      <button
        type="button"
        onClick={toggle}
        aria-label={muted ? 'Unmute video' : 'Mute video'}
        style={{
          position: 'absolute',
          top: 14,
          left: 16,
          width: 40,
          height: 40,
          borderRadius: 999,
          background: 'rgba(0,0,0,0.45)',
          border: '1px solid var(--border)',
          color: 'var(--white)',
          fontSize: 16,
          backdropFilter: 'blur(6px)',
        }}
      >
        {muted ? '🔇' : '🔊'}
      </button>
    </div>
  );
}
